/**
 * EngagementRing — per-event push engagement indicator.
 *
 * Displays a circular SVG ring showing what % of pushed guests
 * returned after the notification. Has two variants:
 *
 *   - admin: shows raw % + subtitle "X% returned after push"
 *   - host:  shows a positive-framing label (no red, no "weak")
 *
 * Never renders when sample_size < 10 (caller should also guard,
 * backend returns null).
 */
import React from 'react';

const TIER_COLOR = {
  weak:      '#ef4444',
  ok:        '#f59e0b',
  strong:    '#10b981',
  excellent: '#22c55e',
};

const TIER_LABEL_HOST = {
  weak:      'Getting started',
  ok:        'Building momentum',
  strong:    'Pulling people back 🎉',
  excellent: 'Thriving 🔥',
};

const EngagementRing = ({
  rate,              // 0..1
  ratePct,           // optional pre-computed %, else derived from rate
  sampleSize = 0,
  tier,              // 'weak'|'ok'|'strong'|'excellent'
  size = 56,
  stroke = 6,
  variant = 'admin', // 'admin' | 'host'
  label,             // optional override
  testId,
}) => {
  if (rate == null || isNaN(rate)) return null;

  const pct = Math.round(ratePct != null ? ratePct : rate * 100);
  const color = TIER_COLOR[tier] || TIER_COLOR.ok;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (pct / 100);
  const gap = c - dash;

  const hostLabel = TIER_LABEL_HOST[tier] || 'Growing';
  const primaryLabel = label || (variant === 'admin' ? `${pct}%` : hostLabel);
  const subtitle = variant === 'admin'
    ? `${pct}% returned after push`
    : `${pct}% of guests came back`;

  return (
    <div className="inline-flex items-center gap-3" data-testid={testId || 'engagement-ring'}>
      <svg width={size} height={size} className="-rotate-90 shrink-0" aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r}
                stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r}
                stroke={color} strokeWidth={stroke} fill="none"
                strokeDasharray={`${dash} ${gap}`} strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 600ms ease-out' }} />
      </svg>
      <div className="flex flex-col min-w-0">
        <div className="text-sm font-black text-white leading-tight truncate" data-testid="ring-label">
          {primaryLabel}
        </div>
        <div className="text-[11px] text-white/55 truncate" data-testid="ring-subtitle">
          {subtitle}{variant === 'admin' && sampleSize ? ` · n=${sampleSize}` : ''}
        </div>
      </div>
    </div>
  );
};

export default EngagementRing;
